/**
 * Encryption utilities for PIN-protected notes
 * Uses Web Crypto API for AES-256-GCM encryption
 */

// Convert string to ArrayBuffer
const stringToBuffer = (str) => {
  return new TextEncoder().encode(str);
};

// Convert ArrayBuffer to string
const bufferToString = (buffer) => {
  return new TextDecoder().decode(buffer);
};

// Convert ArrayBuffer to base64 string
const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// Convert base64 string to ArrayBuffer
const base64ToBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Generate a random salt for key derivation
 * @returns {string} Base64-encoded salt
 */
export const generateSalt = () => {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return bufferToBase64(salt);
};

/**
 * Derive an encryption key from PIN using PBKDF2
 * @param {string} pin - User's PIN
 * @param {string} salt - Base64-encoded salt
 * @returns {Promise<CryptoKey>} Derived AES-256-GCM key
 */
export const deriveKey = async (pin, salt) => {
  const pinBuffer = stringToBuffer(pin);
  const saltBuffer = base64ToBuffer(salt);

  // Import PIN as key material
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    pinBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive AES-256-GCM key using PBKDF2
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000, // 100k iterations for security
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false, // Not extractable
    ['encrypt', 'decrypt']
  );

  return key;
};

/**
 * Hash PIN for verification (not for encryption)
 * @param {string} pin - User's PIN
 * @param {string} salt - Base64-encoded salt
 * @returns {Promise<string>} Base64-encoded hash
 */
export const hashPin = async (pin, salt) => {
  const combined = pin + salt;
  const buffer = stringToBuffer(combined);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return bufferToBase64(hashBuffer);
};

/**
 * Encrypt data using AES-256-GCM
 * @param {string} plaintext - Data to encrypt (JSON string)
 * @param {CryptoKey} key - AES-256-GCM key
 * @returns {Promise<{iv: string, ciphertext: string}>} Encrypted data
 */
export const encrypt = async (plaintext, key) => {
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
  const plaintextBuffer = stringToBuffer(plaintext);

  const ciphertextBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    key,
    plaintextBuffer
  );

  return {
    iv: bufferToBase64(iv),
    ciphertext: bufferToBase64(ciphertextBuffer),
  };
};

/**
 * Decrypt data using AES-256-GCM
 * @param {string} ciphertext - Base64-encoded ciphertext
 * @param {string} iv - Base64-encoded IV
 * @param {CryptoKey} key - AES-256-GCM key
 * @returns {Promise<string>} Decrypted plaintext
 */
export const decrypt = async (ciphertext, iv, key) => {
  const ciphertextBuffer = base64ToBuffer(ciphertext);
  const ivBuffer = base64ToBuffer(iv);

  const plaintextBuffer = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ivBuffer,
    },
    key,
    ciphertextBuffer
  );

  return bufferToString(plaintextBuffer);
};

/**
 * Encrypt snippets for a protected note
 * @param {Array} snippets - Array of snippet objects
 * @param {string} pin - User's PIN
 * @param {string} salt - Base64-encoded salt
 * @returns {Promise<{iv: string, ciphertext: string, snippetIds: string[]}>}
 */
export const encryptSnippets = async (snippets, pin, salt) => {
  const key = await deriveKey(pin, salt);

  // Extract sensitive data from snippets
  const sensitiveData = snippets.map(s => ({
    id: s.id,
    title: s.title,
    content: s.content,
  }));

  const plaintext = JSON.stringify(sensitiveData);
  const encrypted = await encrypt(plaintext, key);

  return {
    ...encrypted,
    snippetIds: snippets.map(s => s.id),
  };
};

/**
 * Decrypt snippets for a protected note
 * @param {{iv: string, ciphertext: string, snippetIds: string[]}} encryptedData
 * @param {string} pin - User's PIN
 * @param {string} salt - Base64-encoded salt
 * @returns {Promise<Array>} Decrypted snippet data
 */
export const decryptSnippets = async (encryptedData, pin, salt) => {
  const key = await deriveKey(pin, salt);
  const plaintext = await decrypt(encryptedData.ciphertext, encryptedData.iv, key);
  return JSON.parse(plaintext);
};

/**
 * Verify PIN against stored hash
 * @param {string} pin - PIN to verify
 * @param {string} storedHash - Stored hash to compare against
 * @param {string} salt - Salt used for hashing
 * @returns {Promise<boolean>} True if PIN matches
 */
export const verifyPinHash = async (pin, storedHash, salt) => {
  const computedHash = await hashPin(pin, salt);
  return computedHash === storedHash;
};
